import { readValues, writeValues } from "../../shared/lib/urlParams";

/**
 * The gallery's shareable filter state (`/gallery?source&type&owner`). Type and
 * source are single values; owners repeat and OR together. Unknown values fall
 * back to All so stale links keep rendering every photo. An empty owner value is
 * the "Unassigned" selection and is preserved.
 */
export type PhotoFilter = "all" | "general" | "nutrition";
export type PhotoSource = "all" | "containers" | "items";

export interface GalleryFilterState {
  filter: PhotoFilter;
  source: PhotoSource;
  owners: string[];
}

export const DEFAULT_GALLERY_FILTERS: GalleryFilterState = {
  filter: "all",
  source: "all",
  owners: [],
};

/**
 * Read the gallery filter state from a query string.
 *
 * @param params the URL search params.
 * @returns validated filters; unknown source/type values fall back to All.
 */
export function readGalleryFilters(params: URLSearchParams): GalleryFilterState {
  const source = params.get("source");
  const type = params.get("type");
  const resolvedSource = isSource(source) ? source : "all";
  return {
    // Container photos carry no item type, so a container-only view cannot keep
    // a Type selection (the row is hidden there).
    filter: resolvedSource === "containers" || !isFilter(type) ? "all" : type,
    source: resolvedSource,
    owners: readValues(params, "owner"),
  };
}

/**
 * Write the gallery filter state onto a copy of the current params, removing
 * defaults and preserving unrelated parameters.
 *
 * @param current the existing URL params.
 * @param state the filters to encode.
 * @returns a new {@link URLSearchParams} safe to navigate to.
 */
export function writeGalleryFilters(
  current: URLSearchParams,
  state: GalleryFilterState,
): URLSearchParams {
  const params = new URLSearchParams(current);
  if (state.source === "all") params.delete("source");
  else params.set("source", state.source);
  if (state.filter === "all") params.delete("type");
  else params.set("type", state.filter);
  writeValues(params, "owner", state.owners);
  return params;
}

function isFilter(value: string | null): value is PhotoFilter {
  return value === "general" || value === "nutrition";
}

function isSource(value: string | null): value is PhotoSource {
  return value === "containers" || value === "items";
}
